import { useCallback, useEffect, useRef, useState } from 'react';
import type { Falha, Resultado } from '../../acesso';
import type { Bloco } from '../../encomendas';

/**
 * Carregamento de UMA coluna da consulta: loading, erro e "tentar de novo" próprios. A falha pode
 * ser do InovaPay (rede, sessão) ou da fonte (o bloco veio `ok: false`) — as duas aparecem na
 * coluna, nunca derrubam a tela.
 */
export type EstadoDoBloco<T> =
  | { readonly fase: 'carregando' }
  | { readonly fase: 'pronto'; readonly dados: T; readonly lidoEm: string }
  | { readonly fase: 'falha'; readonly mensagem: string };

export function usarBloco<R, T>(
  carregar: (forcar: boolean) => Promise<Resultado<R>>,
  extrair: (r: R) => Bloco<T>,
  textoDaFalha: (f: Falha) => string,
  chave: string,
  /** Relê sozinho a cada tantos segundos, sem apagar o que está na tela. */
  atualizarACada?: number,
): { estado: EstadoDoBloco<T>; recarregar: (forcar?: boolean) => void; atualizando: boolean } {
  const [estado, setEstado] = useState<EstadoDoBloco<T>>({ fase: 'carregando' });
  const [atualizando, setAtualizando] = useState(false);
  const geracao = useRef(0);
  const carregarRef = useRef(carregar);
  carregarRef.current = carregar;
  const extrairRef = useRef(extrair);
  extrairRef.current = extrair;

  const recarregar = useCallback(
    (forcar = false) => {
      const g = ++geracao.current;
      // Atualizar não apaga o que está na tela: mostra "atualizando" por cima (não trava a tela).
      setEstado((e) => (e.fase === 'pronto' ? e : { fase: 'carregando' }));
      setAtualizando(true);
      void carregarRef.current(forcar).then((r) => {
        if (g !== geracao.current) return;
        setAtualizando(false);
        if (!r.ok) {
          setEstado({ fase: 'falha', mensagem: textoDaFalha(r.falha) });
          return;
        }
        const b = extrairRef.current(r.dados);
        setEstado(
          b.ok
            ? { fase: 'pronto', dados: b.dados, lidoEm: b.lidoEm }
            : { fase: 'falha', mensagem: b.mensagem },
        );
      });
    },
    [textoDaFalha],
  );

  useEffect(() => {
    setEstado({ fase: 'carregando' });
    recarregar();
    return () => {
      geracao.current++;
    };
  }, [chave, recarregar]);

  useEffect(() => {
    if (!atualizarACada) return;
    const t = window.setInterval(() => recarregar(), atualizarACada * 1_000);
    return () => window.clearInterval(t);
  }, [atualizarACada, chave, recarregar]);

  return { estado, recarregar, atualizando };
}
